"use client";

import { useState } from "react";
import { useQuery, useMutation } from "convex/react";
import { api } from "@/convex/_generated/api";
import { Id } from "@/convex/_generated/dataModel";
import { motion, AnimatePresence } from "framer-motion";
import {
  Upload,
  X,
  Loader2,
  CheckCircle2,
  AlertCircle,
} from "lucide-react";

const REQUEST_TYPES = [
  { value: "bug_report", label: "Bug Report" },
  { value: "feature_request", label: "Feature Request" },
  { value: "ui_issue", label: "UI Issue" },
  { value: "logic_issue", label: "Logic Issue" },
  { value: "performance_issue", label: "Performance Issue" },
  { value: "security_issue", label: "Security Issue" },
] as const;

const PRIORITY_LEVELS = [
  { value: "trivial", label: "Trivial", color: "blue" },
  { value: "minor", label: "Minor", color: "green" },
  { value: "major", label: "Major", color: "yellow" },
  { value: "critical", label: "Critical", color: "orange" },
  { value: "urgent", label: "Urgent", color: "red" },
] as const;

interface FilePreview {
  file: File;
  preview: string;
  id: string;
}

export function SupportForm() {
  const [formData, setFormData] = useState({
    projectId: "" as Id<"projects"> | "",
    submitterName: "",
    submitterEmail: "",
    submitterPhone: "",
    requestType: "bug_report" as const,
    priority: "minor" as "trivial" | "minor" | "major" | "critical" | "urgent",
    subject: "",
    description: "",
  });

  const [filePreviews, setFilePreviews] = useState<FilePreview[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{
    type: "success" | "error" | null;
    message: string;
  }>({ type: null, message: "" });

  // Fetch active projects using Convex
  const projects = useQuery(api.projects.queries.listActiveProjects);
  const submitTicket = useMutation(api.supportTickets.mutations.submitSupportTicket);
  const generateUploadUrl = useMutation(api.media.imageStorage.generateUploadUrl);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const maxSize = 5 * 1024 * 1024; // 5MB

    const validFiles: FilePreview[] = [];

    for (const file of files) {
      if (file.size > maxSize) {
        setSubmitStatus({
          type: "error",
          message: `File "${file.name}" exceeds 5MB limit`,
        });
        continue;
      }

      if (!file.type.startsWith("image/")) {
        setSubmitStatus({
          type: "error",
          message: `File "${file.name}" is not an image`,
        });
        continue;
      }

      const preview = URL.createObjectURL(file);
      validFiles.push({
        file,
        preview,
        id: Math.random().toString(36).substring(7),
      });
    }

    setFilePreviews((prev) => [...prev, ...validFiles]);
  };

  const removeFile = (id: string) => {
    setFilePreviews((prev) => {
      const fileToRemove = prev.find((f) => f.id === id);
      if (fileToRemove) {
        URL.revokeObjectURL(fileToRemove.preview);
      }
      return prev.filter((f) => f.id !== id);
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!formData.projectId) {
      setSubmitStatus({ type: "error", message: "Please select a project" });
      return;
    }

    if (!formData.submitterName || !formData.submitterEmail) {
      setSubmitStatus({
        type: "error",
        message: "Please fill in all required fields",
      });
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus({ type: null, message: "" });

    try {
      // Upload attachments
      const attachmentStorageIds: string[] = [];
      const attachmentUrls: string[] = [];

      for (const fp of filePreviews) {
        try {
          // Get upload URL
          const uploadUrl = await generateUploadUrl();

          // Upload file
          const result = await fetch(uploadUrl, {
            method: "POST",
            headers: { "Content-Type": fp.file.type },
            body: fp.file,
          });

          const { storageId } = await result.json();
          attachmentStorageIds.push(storageId);

          // Generate public URL (will be generated on backend)
          attachmentUrls.push("");
        } catch (error) {
          console.error("Failed to upload file:", fp.file.name, error);
        }
      }

      // Submit ticket
      await submitTicket({
        projectId: formData.projectId as Id<"projects">,
        submitterName: formData.submitterName,
        submitterEmail: formData.submitterEmail,
        submitterPhone: formData.submitterPhone || undefined,
        requestType: formData.requestType,
        priority: formData.priority,
        subject: formData.subject,
        description: formData.description,
        attachmentStorageIds,
        attachmentUrls,
      });

      setSubmitStatus({
        type: "success",
        message:
          "Support ticket submitted successfully! Check your email for confirmation.",
      });

      // Reset form
      setFormData({
        projectId: "" as Id<"projects"> | "",
        submitterName: "",
        submitterEmail: "",
        submitterPhone: "",
        requestType: "bug_report",
        priority: "minor",
        subject: "",
        description: "",
      });
      setFilePreviews([]);
    } catch (error) {
      console.error("Submit error:", error);
      setSubmitStatus({
        type: "error",
        message: error instanceof Error ? error.message : "Failed to submit ticket",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Project Selection */}
      <div>
        <label className="block text-sm font-medium text-white mb-2">
          Project <span className="text-red-400">*</span>
        </label>
        <select
          value={formData.projectId}
          onChange={(e) =>
            setFormData({ ...formData, projectId: e.target.value as any })
          }
          className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:ring-2 focus:ring-kuttos-cyan focus:border-transparent transition-all [&>option]:bg-[#0a0a0f] [&>option]:text-white"
          required
          disabled={projects === undefined}
        >
          <option value="">Select a project</option>
          {projects?.map((project: any) => (
            <option key={project._id} value={project._id}>
              {project.name}
            </option>
          ))}
        </select>
      </div>

      {/* Request Type */}
      <div>
        <label className="block text-sm font-medium text-white mb-2">
          Request Type <span className="text-red-400">*</span>
        </label>
        <select
          value={formData.requestType}
          onChange={(e) =>
            setFormData({
              ...formData,
              requestType: e.target.value as any,
            })
          }
          className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:ring-2 focus:ring-kuttos-cyan focus:border-transparent transition-all [&>option]:bg-[#0a0a0f] [&>option]:text-white"
          required
        >
          {REQUEST_TYPES.map((type) => (
            <option key={type.value} value={type.value}>
              {type.label}
            </option>
          ))}
        </select>
      </div>

      {/* Priority Level */}
      <div>
        <label className="block text-sm font-medium text-white mb-2">
          Priority Level <span className="text-red-400">*</span>
        </label>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          {PRIORITY_LEVELS.map((level) => (
            <button
              key={level.value}
              type="button"
              onClick={() =>
                setFormData({ ...formData, priority: level.value })
              }
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${formData.priority === level.value
                  ? `bg-${level.color}-500/20 text-${level.color}-400 ring-2 ring-${level.color}-500/50`
                  : "bg-white/5 text-gray-400 hover:bg-white/10"
                }`}
            >
              {level.label}
            </button>
          ))}
        </div>
      </div>

      {/* Subject */}
      <div>
        <label className="block text-sm font-medium text-white mb-2">
          Subject <span className="text-red-400">*</span>
        </label>
        <input
          type="text"
          value={formData.subject}
          onChange={(e) =>
            setFormData({ ...formData, subject: e.target.value })
          }
          className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-kuttos-cyan focus:border-transparent transition-all"
          placeholder="Brief summary of your request"
          required
        />
      </div>

      {/* Description */}
      <div>
        <label className="block text-sm font-medium text-white mb-2">
          Description <span className="text-red-400">*</span>
        </label>
        <textarea
          value={formData.description}
          onChange={(e) =>
            setFormData({ ...formData, description: e.target.value })
          }
          className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-kuttos-cyan focus:border-transparent transition-all resize-none"
          rows={6}
          placeholder="Please provide detailed information about your request..."
          required
        />
      </div>

      {/* File Upload */}
      <div>
        <label className="block text-sm font-medium text-white mb-2">
          Attachments (Screenshots/Images)
        </label>
        <div className="border-2 border-dashed border-white/10 rounded-lg p-6 text-center hover:border-kuttos-cyan/50 transition-colors bg-white/5">
          <input
            type="file"
            id="file-upload"
            multiple
            accept="image/*"
            onChange={handleFileChange}
            className="hidden"
          />
          <label
            htmlFor="file-upload"
            className="cursor-pointer inline-flex flex-col items-center"
          >
            <Upload className="w-12 h-12 text-gray-400 mb-3" />
            <span className="text-sm text-gray-400">
              Click to upload or drag and drop
            </span>
            <span className="text-xs text-gray-500 mt-1">
              PNG, JPG, GIF, WebP up to 5MB each
            </span>
          </label>
        </div>

        {/* File Previews */}
        <AnimatePresence>
          {filePreviews.length > 0 && (
            <div className="mt-4 grid grid-cols-4 gap-4">
              {filePreviews.map((fp) => (
                <motion.div
                  key={fp.id}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  className="relative group aspect-square rounded-lg overflow-hidden border border-white/10"
                >
                  <img
                    src={fp.preview}
                    alt={fp.file.name}
                    className="w-full h-full object-cover"
                  />
                  <button
                    type="button"
                    onClick={() => removeFile(fp.id)}
                    className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X className="w-4 h-4" />
                  </button>
                  <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-xs p-1 truncate">
                    {fp.file.name}
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </AnimatePresence>
      </div>

      {/* Contact Information */}
      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-white mb-2">
            Your Name <span className="text-red-400">*</span>
          </label>
          <input
            type="text"
            value={formData.submitterName}
            onChange={(e) =>
              setFormData({ ...formData, submitterName: e.target.value })
            }
            className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-kuttos-cyan focus:border-transparent transition-all"
            placeholder="John Doe"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-white mb-2">
            Email Address <span className="text-red-400">*</span>
          </label>
          <input
            type="email"
            value={formData.submitterEmail}
            onChange={(e) =>
              setFormData({ ...formData, submitterEmail: e.target.value })
            }
            className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-kuttos-cyan focus:border-transparent transition-all"
            placeholder="john@example.com"
            required
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-white mb-2">
          Phone Number (Optional)
        </label>
        <input
          type="tel"
          value={formData.submitterPhone}
          onChange={(e) =>
            setFormData({ ...formData, submitterPhone: e.target.value })
          }
          className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-kuttos-cyan focus:border-transparent transition-all"
          placeholder="+250 7XX XXX XXX"
        />
      </div>

      {/* Status Messages */}
      <AnimatePresence>
        {submitStatus.type && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className={`flex items-center gap-3 p-4 rounded-lg ${submitStatus.type === "success"
                ? "bg-green-500/20 text-green-400 border border-green-500/50"
                : "bg-red-500/20 text-red-400 border border-red-500/50"
              }`}
          >
            {submitStatus.type === "success" ? (
              <CheckCircle2 className="w-5 h-5 flex-shrink-0" />
            ) : (
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
            )}
            <p className="text-sm">{submitStatus.message}</p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Submit Button */}
      <button
        type="submit"
        disabled={isSubmitting}
        className="w-full btn-cyan py-4 px-6 rounded-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
      >
        {isSubmitting ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            Submitting...
          </>
        ) : (
          "Submit Support Ticket"
        )}
      </button>
    </form>
  );
}
